import { Component, computed, inject, signal } from '@angular/core';
import { PermissionService } from '../../permissions/permission.service';
import { DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MediaAssetDto, MediaService } from '../../../shared/services/media.service';
import {
  AdminButtonComponent,
  AdminCardComponent,
  AdminEmptyStateComponent,
} from '../../../shared/admin';

@Component({
  selector: 'app-media-page',
  standalone: true,
  imports: [DatePipe, DecimalPipe, FormsModule, AdminCardComponent, AdminButtonComponent, AdminEmptyStateComponent],
  template: `
    <section>
      <app-admin-card title="Upload" icon="image">
        <div class="mt-3 grid gap-3 sm:grid-cols-3">
          <label class="block text-sm sm:col-span-2">
            <span class="cg-admin-field__label">File</span>
            <input type="file" class="cg-input" (change)="onFile($event)" />
          </label>
          <label class="block text-sm">
            <span class="cg-admin-field__label">Alt text</span>
            <input class="cg-input" [(ngModel)]="altText" name="altText" />
          </label>
        </div>
        <div class="cg-admin-page-header__actions" style="margin-top: var(--cg-space-4)">
          @if (showUpload()) {
            <app-admin-button variant="accent" type="button" (click)="upload()" [disabled]="!canUpload() || !file() || uploading()">
              {{ uploading() ? 'Uploading...' : 'Upload to Bunny' }}
            </app-admin-button>
          }
          @if (message(); as msg) {
            <span class="cg-form-error" [class.cg-form-success]="!error()">{{ msg }}</span>
          }
        </div>
      </app-admin-card>

      <app-admin-card title="Assets">
        @if (assets().length === 0) {
          <app-admin-empty-state icon="image" title="No assets yet" />
        }
        <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          @for (asset of assets(); track asset._id) {
            <div class="cg-admin-card" style="box-shadow: var(--cg-shadow-subtle); padding: var(--cg-space-4)">
              @if (asset.mimeType.startsWith('image/')) {
                <img [src]="asset.url" alt="" class="h-32 w-full rounded object-cover" />
              } @else {
                <div class="flex h-32 items-center justify-center rounded bg-slate-100 text-xs text-slate-500">
                  {{ asset.mimeType }}
                </div>
              }
              <div class="mt-2 truncate text-sm font-medium" [title]="asset.originalName">{{ asset.originalName }}</div>
              <div class="text-xs text-slate-500">{{ asset.createdAt | date:'short' }} · {{ asset.size | number }} B</div>
              <div class="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <app-admin-button variant="secondary" type="button" (click)="copy(asset.url)">Copy URL</app-admin-button>
                <span class="cg-form-help">ID: <code class="cg-input--mono">{{ asset._id }}</code></span>
                @if (showDelete()) {
                  <app-admin-button variant="danger" type="button" (click)="remove(asset._id)" [disabled]="!canDelete()">Delete</app-admin-button>
                }
              </div>
            </div>
          }
        </div>
      </app-admin-card>
    </section>
  `,
})
export class MediaPageComponent {
  private readonly mediaService = inject(MediaService);
  private readonly permissions = inject(PermissionService);

  readonly showUpload = computed(() => this.permissions.canShow('website.media', 'upload'));
  readonly canUpload = computed(() => this.permissions.canExecute('website.media', 'upload'));
  readonly showDelete = computed(() => this.permissions.canShow('website.media', 'delete'));
  readonly canDelete = computed(() => this.permissions.canExecute('website.media', 'delete'));

  readonly assets = signal<MediaAssetDto[]>([]);
  readonly file = signal<File | null>(null);
  readonly uploading = signal(false);
  readonly message = signal<string | null>(null);
  readonly error = signal(false);
  altText = '';

  constructor() {
    this.refresh();
  }

  refresh(): void {
    this.mediaService.list().subscribe((items) => this.assets.set(items));
  }

  onFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.file.set(input.files?.[0] ?? null);
  }

  upload(): void {
    const file = this.file();
    if (!file) return;
    this.uploading.set(true);
    this.error.set(false);
    this.message.set(null);
    this.mediaService.upload(file, { altText: this.altText || undefined }).subscribe({
      next: () => {
        this.uploading.set(false);
        this.altText = '';
        this.file.set(null);
        this.message.set('Uploaded.');
        setTimeout(() => this.message.set(null), 2000);
        this.refresh();
      },
      error: (err) => {
        this.uploading.set(false);
        this.error.set(true);
        this.message.set(err?.error?.message || 'Upload failed.');
      },
    });
  }

  remove(id: string): void {
    if (!confirm('Delete this asset (also removes from Bunny)?')) return;
    this.mediaService.remove(id).subscribe(() => this.refresh());
  }

  copy(url: string): void {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(url);
      this.message.set('URL copied.');
      setTimeout(() => this.message.set(null), 1500);
    }
  }
}
